import { z } from "zod";

export const accessStatusSubscriptionSchema = z.object({
  publicId: z.string(),
  planName: z.string(),
  startDate: z.string(),
  endDate: z.string(),
  status: z.string(),
  remainingDays: z.number(),
});
export type AccessStatusSubscription = z.infer<
  typeof accessStatusSubscriptionSchema
>;

export const accessStatusUpcomingSchema = z.object({
  planName: z.string(),
  startDate: z.string(),
  endDate: z.string(),
  remainingDays: z.number(),
});
export type AccessStatusUpcoming = z.infer<typeof accessStatusUpcomingSchema>;

export const accessStatusSchema = z.object({
  client: z.object({
    publicId: z.string(),
    firstName: z.string(),
    lastName: z.string().nullish(),
    email: z.string().nullish(),
    birthDate: z.string().nullable(),
    profilePictureKey: z.string().nullable(),
  }),
  canAccess: z.boolean(),
  isExpiringSoon: z.boolean(),
  subscription: accessStatusSubscriptionSchema.nullable(),
  upcomingSubscription: accessStatusUpcomingSchema.nullable(),
  lastAccess: z.object({ createdAt: z.string() }).nullable(),
  recentAccessAlert: z.boolean(),
  alertMessage: z.string().nullable(),
});
export type AccessStatus = z.infer<typeof accessStatusSchema>;

export const realtimeAccessEventSchema = z.object({
  clientName: z.string(),
  clientPublicId: z.string().nullable(),
  isException: z.boolean(),
  exceptionReason: z.string().nullable(),
  enteredAt: z.string(),
});
export type RealtimeAccessEvent = z.infer<typeof realtimeAccessEventSchema>;

export const registerAccessResponseSchema = z.object({
  publicId: z.string(),
  clientPublicId: z.string(),
  clientName: z.string(),
  createdAt: z.string(),
});
export type RegisterAccessResponse = z.infer<
  typeof registerAccessResponseSchema
>;

export const activeClientEntrySchema = z.object({
  publicId: z.string().nullable(),
  firstName: z.string(),
  lastName: z.string().nullish(),
  profilePictureKey: z.string().nullable(),
  enteredAt: z.string(),
  isException: z.boolean(),
  exceptionReason: z.string().nullable(),
  isGuest: z.boolean().optional().default(false),
});
export type ActiveClientEntry = z.infer<typeof activeClientEntrySchema>;

export const activeClientsResponseSchema = z.object({
  clients: z.array(activeClientEntrySchema),
  total: z.number(),
  limit: z.number(),
  offset: z.number(),
});
export type ActiveClientsResponse = z.infer<typeof activeClientsResponseSchema>;

export const accessEntriesV2ResponseSchema = z.object({
  data: z.array(activeClientEntrySchema),
  pagination: z.object({
    totalCount: z.number(),
    perPage: z.number(),
  }),
});

export const accessLogEntrySchema = z.object({
  publicId: z.string(),
  createdAt: z.string(),
  isException: z.boolean(),
  exceptionReason: z.string().nullable(),
  registeredBy: z.string().nullable(),
});
export type AccessLogEntry = z.infer<typeof accessLogEntrySchema>;

export const clientAccessLogsResponseSchema = z.object({
  logs: z.array(accessLogEntrySchema),
  total: z.number(),
  limit: z.number(),
  offset: z.number(),
  lastAccess: z.string().nullable(),
  weeklyAverage: z.number(),
  dowDistribution: z.array(z.object({ slot: z.number(), count: z.number() })),
  hourDistribution: z.array(z.object({ slot: z.number(), count: z.number() })),
});
export type ClientAccessLogsResponse = z.infer<
  typeof clientAccessLogsResponseSchema
>;

export const clientFaceDataSchema = z.object({
  publicId: z.string(),
  firstName: z.string(),
  lastName: z.string().nullish(),
  profilePictureKey: z.string().nullable(),
  descriptor: z.array(z.number()).nullable(),
  descriptorSourceKey: z.string().nullable(),
});
export type ClientFaceData = z.infer<typeof clientFaceDataSchema>;

export const faceDescriptorsResponseSchema = z.object({
  clients: z.array(clientFaceDataSchema),
  generatedAt: z.string(),
});
export type FaceDescriptorsResponse = z.infer<
  typeof faceDescriptorsResponseSchema
>;
